import { PropsWithChildren } from 'react';

import { Box, Flex } from '@opendoor/bricks/core';

import { CheckboxGroupQuestion, RadioGroupQuestion } from 'src/lib/flow/FlowQuestion';
import { AnswerSet } from 'src/store/sellerFlow/AnswerSet';

import { noneKey } from '../../helpers/questions';
import { CheckboxRadioProps } from '../common/CheckboxGroup';
import InfoCard from '../common/InfoCard';
import { Questions } from '../common/Question';

const FollowUp: React.FC<PropsWithChildren> = ({ children }) => (
  <Flex
    onClick={(evt) => {
      evt.stopPropagation();
    }}
    flexDir="column"
    cursor="pointer"
    paddingBlock={4}
  >
    {children}
  </Flex>
);

/**
 * For radio and checkbox groups, build out the `options` array. This
 * recursively generates any follow up questions and info cards for items that
 * are selected/checked.
 */
export const getOptions = (
  options: CheckboxGroupQuestion['options'] | RadioGroupQuestion['options'] = [],
  answers: AnswerSet,
  onAnswerChange: (name: string, value: any, hasError?: boolean, errorMessage?: string) => void,
  hasError: boolean,
  pageHasError: boolean,
  isFollowUp: boolean,
  selectedValues: Array<string | number> = [],
  showNone = false,
): Array<CheckboxRadioProps> => {
  const questionOptions = options.filter((q) => !q?.hidden);
  const allOptions = showNone
    ? [...questionOptions, { label: 'None of the above', value: noneKey }]
    : questionOptions;

  return allOptions?.map((flowOption) => {
    let label: React.ReactNode;
    label = (
      <Box as="div" fontWeight="semibold" fontSize="s1" ml={2} pr={4}>
        {flowOption.label}
      </Box>
    );
    if (isFollowUp) {
      label = (
        <Box as="div" fontWeight="semibold" fontSize="s1" ml={2}>
          {flowOption.label}
        </Box>
      );
    }

    if (flowOption.description) {
      label = (
        <Box ml={2}>
          <Box as="div" fontSize="s1" mb={2} fontWeight="semibold">
            {flowOption.label}
          </Box>
          <Box as="div" fontSize="s0" fontWeight="regular">
            {flowOption.description}
          </Box>
        </Box>
      );
    }

    const option: CheckboxRadioProps = {
      label,
      value: String(flowOption.value),
    };

    let hasFollowUps = false;
    let hasInfoCard = false;

    if (typeof flowOption.value !== 'object' && selectedValues.includes(flowOption.value)) {
      hasFollowUps = !!flowOption?.followUps?.some((f) => f?.hidden !== true);
      hasInfoCard = !!flowOption?.followUpInfo;
    }

    if (hasInfoCard || hasFollowUps) {
      option.additionalContainerChildren = (
        <>
          {hasFollowUps && (
            <FollowUp>
              <Questions
                isNestedFollowup={isFollowUp}
                // FIXME - force type because FlowQuestion and IBaseQuestion aren't totally compatible
                // FlowQuestion describes our parsed json schema, IBaseQuestion
                // seems to describe the parsed schema + jsoneval on fields
                // like hidden and optional
                questions={flowOption.followUps || []}
                //TODO - see if we can remove this, and have things that need answers to yank them from useSelector
                answers={answers}
                onAnswerChange={onAnswerChange}
                hasError={hasError}
                pageHasError={pageHasError}
              />
            </FollowUp>
          )}
          {hasInfoCard && <InfoCard text={String(flowOption.followUpInfo)} />}
        </>
      );
    }

    option.onChange = () => {};
    return option;
  });
};
